import { useQuery } from '@apollo/client'
import HoverTooltip from '@deltek/specification-client/core/Feedbacks/HoverTooltip'
import { ProjectElementNode } from '@deltek/specification-client/core/Models/productListingDetail'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import { Badge, IconButton, makeStyles, Theme } from '@material-ui/core'
import { GET_CLASH_COUNT } from 'api/graphQL/queries/bestPracticeClash.queries'
import clsx from 'clsx'
import ProjectWorkspaceAskDelaChat from 'components/AskDelaChat/ProjectWorkspaceAskDelaChat'
import ChatLauncherIcon from 'components/RightPane/ChatLauncherIcon'
import ClashIcon from 'components/RightPane/ClashIcon'
import KeynotesIcon from 'components/RightPane/KeynotesIcon'
import ProjectPropertiesIcon from 'components/RightPane/ProjectPropertiesIcon'
import TrackChangeIcon from 'components/RightPane/TrackChangeIcon'
import { ProductFamilyStudioManagement } from 'models/StudioManagement/studioManagement'
import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { canShowBestPracticeSetting } from 'shared/utils/bestPracticeUtils'
import { ClashesPane } from './ClashesPane'
import { ProjectProperties } from './ProjectProperties'
import { ToggleHandlerType } from './SidebarToggles'
import { TrackChangesPane } from './TrackChangesPane'
interface RightPaneStyle {
    showRightMostPane: boolean
    currentPane: string
}
const useStyles = makeStyles((theme: Theme) => ({
    tabLabel: {
        fontSize: pxToRem(13),
        textTransform: 'uppercase',
        width: pxToRem(75),
        textAlign: 'justify',
        lineHeight: pxToRem(15),
    },
    defaultTextColor: {
        color: '#A78966',
    },
    selectedTabText: {
        color: '#04325B',
    },
    tabBorder: {
        borderBottom: '1px solid #DADADA',
    },
    selectedTab: {
        borderBottom: '3px solid #304959',
    },
    tabRoot: {
        minWidth: pxToRem(125),
        maxWidth: pxToRem(125),
    },
    fullWidthRightPaneKeynotesContainer: {
        borderRadius: '5px 0 0 5px',
        border: '1px solid #C6C6C6',
        borderRight: 'none',
        width: (props: RightPaneStyle) =>
            props.showRightMostPane
                ? props.currentPane === 'askDela'
                    ? pxToRem(400)
                    : pxToRem(305)
                : pxToRem(36),
        display: 'flex',
        height: '100%',
        overflow: 'hidden',
        backgroundColor: '#ffffff',
        boxShadow: '0px 1px 4px 0px rgba(0, 0, 0, 0.25)',
        '&:focus': {
            backgroundColor: theme.palette.common.white,
        },
        '&::-webkit-scrollbar': {
            width: 6,
            height: 6,
        },
        '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#464646',
            borderRadius: 100,
        },
        '&::-webkit-scrollbar-track': {
            boxShadow: 'none',
            marginTop: theme.spacing(2.5),
        },
        transition: 'width 330ms ease-in-out',
    },
    askDelaWidth: {
        width: `${pxToRem(400)} !important`,
    },
    buttonContainer: {
        width: pxToRem(36),
        background: '#ffffff',
        paddingTop: pxToRem(50),
        borderRight: '1px solid #cfcfcfd9',
    },
    rightPaneButton: {
        margin: pxToRem(3),
        marginBottom: pxToRem(10),
        width: pxToRem(30),
        height: pxToRem(30),
        borderRadius: pxToRem(4),
        color: '#ffffff',
        '&:hover': {
            background: '#D5F3FF',
        },
    },
    rightPaneIconActive: {
        fill: '#ffffff',
        '&:hover': {
            fill: '#314959',
        },
    },
    active: {
        background: '#314959',
        '& > svg': {
            fill: '#ffffff',
        },
    },
    rightPaneIcon: {
        fill: '#314959',
        '&:hover': {
            fill: '#314959',
        },
    },

    rightPaneInnerContainer: {
        height: '100%',
        width: '100%',
        background: '#ffffffd9',
    },
    collapseExpandButton: {
        cursor: 'pointer',
        color: '#04325B',
        width: '100%',
        height: 40,
        background: '#F5F5F5',
        borderRadius: 0,
        transition: '100ms',
        borderBottom: '1px solid #DADADA',
        paddingLeft: pxToRem(4),
        '& > .MuiButton-label': {
            display: 'flex',
            justifyContent: 'start',
            alignItems: 'center',
            '& > span': {
                fontSize: pxToRem(14),
                fontWeight: '500',
                textTransform: 'uppercase',
            },
        },
    },
    keynoteText: {
        textAlign: 'center',
    },
    spacer: {
        flexGrow: 1 /* Take up the remaining space */,
    },
    askDelaButton: {
        position: 'absolute',
        bottom: '12px',
    },
}))

export interface RightMostPaneProps {
    drawingDesignation: string
    isProjectPropertiesHidden: boolean
    isTrackChangesPaneHidden: boolean
    showRightMostPane: boolean
    toggleHandler: (
        toggleType: ToggleHandlerType,
        value: boolean | undefined
    ) => void
    contentTeamFromStudioPage?: boolean
    setLoadingBackdrop?: (value: boolean) => void
    setShowRightMostPane?: (value: boolean) => void
    selectedTopic?: Partial<ProductFamilyStudioManagement>
    setRightMostPaneTab?: (value: string) => void
    selectedProjectElementNode?: ProjectElementNode | null
}

export const RighMostPane: React.FC<RightMostPaneProps> = ({
    drawingDesignation,
    isProjectPropertiesHidden,
    isTrackChangesPaneHidden,
    toggleHandler,
    contentTeamFromStudioPage,
    setLoadingBackdrop,
    selectedTopic,
    showRightMostPane,
    setShowRightMostPane,
    setRightMostPaneTab,
    selectedProjectElementNode,
}) => {
    const showSpecpointCopilot =
        process.env.REACT_APP_ShowSpecpointCopilot === '1'

    const { projectId } = useParams<{ projectId: string }>()
    const [clashCount, setClashCount] = useState(0)
    const showBPSettings = canShowBestPracticeSetting()
    // Only show clash icon in AE projects (not OM/SMM) and when at Family/Product level
    // Also requires SmartProjectCopy feature flag to be enabled
    const shouldShowClashIcon =
        process.env.REACT_APP_EnableSmartProjectCopy === '1' &&
        showBPSettings &&
        !contentTeamFromStudioPage &&
        (selectedProjectElementNode?.elementType === 'PRODUCTFAMILY' ||
            selectedProjectElementNode?.elementType === 'PRODUCTTYPE')

    const [currentPane, setCurrentPane] = useState(
        isTrackChangesPaneHidden && !isProjectPropertiesHidden
            ? 'projectProperties'
            : 'trackChanges'
    )

    const classes = useStyles({ showRightMostPane, currentPane })

    // Query for clash count
    const { data: clashData } = useQuery(GET_CLASH_COUNT, {
        variables: {
            input: {
                projectId: projectId,
                treePath: selectedProjectElementNode?.treePath,
            },
        },
        skip: !projectId || !shouldShowClashIcon,
        fetchPolicy: 'cache-and-network',
    })

    useEffect(() => {
        if (clashData?.bestPracticeClashCount !== undefined) {
            setClashCount(clashData.bestPracticeClashCount)
        }
    }, [clashData])

    const changeTab = React.useCallback(
        (tab: string) => () => {
            setCurrentPane(tab)
            setShowRightMostPane?.(true)
            setRightMostPaneTab?.(tab)
        },
        [setCurrentPane]
    )

    return (
        <>
            <div
                className={clsx(classes.fullWidthRightPaneKeynotesContainer, {
                    [classes.askDelaWidth]:
                        showRightMostPane && currentPane === 'askDela',
                })}
                id="rightPanel"
            >
                <div className={classes.buttonContainer}>
                    <HoverTooltip text="Track Changes" placements={['left']}>
                        <IconButton
                            size="small"
                            onClick={changeTab('trackChanges')}
                            className={clsx(
                                classes.rightPaneButton,
                                currentPane === 'trackChanges' && classes.active
                            )}
                        >
                            <TrackChangeIcon
                                className={
                                    currentPane === 'trackChanges'
                                        ? classes.rightPaneIconActive
                                        : classes.rightPaneIcon
                                }
                            />
                        </IconButton>
                    </HoverTooltip>
                    <HoverTooltip
                        text="Project Properties"
                        placements={['left']}
                    >
                        <IconButton
                            size="small"
                            onClick={changeTab('projectProperties')}
                            className={clsx(
                                classes.rightPaneButton,
                                currentPane === 'projectProperties' &&
                                    classes.active
                            )}
                        >
                            <ProjectPropertiesIcon
                                className={
                                    currentPane === 'projectProperties'
                                        ? classes.rightPaneIconActive
                                        : classes.rightPaneIcon
                                }
                            />
                        </IconButton>
                    </HoverTooltip>

                    {shouldShowClashIcon && (
                        <HoverTooltip
                            text={
                                clashCount > 0
                                    ? `${clashCount} Best Practice clash${
                                          clashCount !== 1 ? 'es' : ''
                                      }`
                                    : 'Best Practice Clashes'
                            }
                            placements={['left']}
                        >
                            <IconButton
                                size="small"
                                onClick={changeTab('clashes')}
                                className={clsx(
                                    classes.rightPaneButton,
                                    currentPane === 'clashes' && classes.active
                                )}
                            >
                                <Badge
                                    badgeContent={clashCount}
                                    color="error"
                                    max={99}
                                    invisible={clashCount === 0}
                                >
                                    <ClashIcon
                                        className={
                                            currentPane === 'clashes'
                                                ? classes.rightPaneIconActive
                                                : classes.rightPaneIcon
                                        }
                                    />
                                </Badge>
                            </IconButton>
                        </HoverTooltip>
                    )}

                    <div className={classes.spacer}></div>

                    {showSpecpointCopilot && (
                        <HoverTooltip
                            text="Ask Dela"
                            placements={['left']}
                            className={classes.askDelaButton}
                        >
                            <IconButton
                                size="small"
                                onClick={changeTab('askDela')}
                                className={clsx(classes.rightPaneButton)}
                            >
                                <ChatLauncherIcon
                                    className={
                                        currentPane === 'askDela'
                                            ? classes.rightPaneIconActive
                                            : classes.rightPaneIcon
                                    }
                                />
                            </IconButton>
                        </HoverTooltip>
                    )}

                    {false && (
                        <IconButton
                            size="small"
                            onClick={changeTab('keynotes')}
                            className={clsx(
                                classes.rightPaneButton,
                                currentPane === 'keynotes' && classes.active
                            )}
                        >
                            <KeynotesIcon
                                className={
                                    currentPane === 'keynotes'
                                        ? classes.rightPaneIconActive
                                        : classes.rightPaneIcon
                                }
                            />
                        </IconButton>
                    )}
                </div>
                {showRightMostPane && (
                    <div className={classes.rightPaneInnerContainer}>
                        {currentPane === 'trackChanges' && true && (
                            <TrackChangesPane
                                selectedTopic={selectedTopic}
                                contentTeamFromStudioPage
                            />
                        )}

                        {currentPane === 'projectProperties' && true && (
                            <ProjectProperties
                                drawingDesignation={drawingDesignation}
                                elementToggleHandler={toggleHandler}
                                contentTeamFromStudioPage={
                                    contentTeamFromStudioPage
                                }
                                setLoadingBackdrop={setLoadingBackdrop}
                            />
                        )}

                        {currentPane === 'clashes' &&
                            process.env.REACT_APP_EnableSmartProjectCopy ===
                                '1' && (
                                <ClashesPane
                                    selectedTopic={selectedTopic}
                                    treePath={
                                        selectedProjectElementNode?.treePath
                                    }
                                />
                            )}

                        {currentPane === 'keynotes' && (
                            <p className={classes.keynoteText}>
                                Key notes will appear here
                            </p>
                        )}

                        {currentPane === 'askDela' &&
                            showRightMostPane &&
                            showSpecpointCopilot && (
                                <ProjectWorkspaceAskDelaChat />
                            )}
                    </div>
                )}
            </div>
        </>
    )
}
